import React from "react";
import { NumericFormat } from "react-number-format";
import { Formik } from "formik";
import * as Yup from "yup";
import "yup-phone";
import { Button } from "@/components/ui/button";
import { updateBusiness } from "@/_actions/businessActions";
import { useStates, useMunicipalities, useNeighborhoods } from "@/_actions/regionActions";
import { handleApiError } from "@/utils/errorHandler";
import { AGENCY_ADDRESS_TYPE, getAgencyAddress } from "../agencyAddress";
import {
  errorClassName,
  fieldGridClassName,
  inputClassName,
  labelClassName,
  outlineButtonClassName,
  primaryButtonClassName,
} from "../styles";

export default function BusinessInformation(props) {
  const realEstateSchema = Yup.object().shape({
    business_name: Yup.string().min(3).required("El nombre es requerido"),
    email_contact: Yup.string()
      .email("Debe ser un correo válido")
      .required("El email es requerido"),
    whatsapp: Yup.string()
      .phone("MX", true, "Debe ser un número de teléfono válido")
      .required("El teléfono es requerido"),
    num_exterior: Yup.string(),
    state: Yup.string().required("El Estado es requerido"),
    municipality: Yup.string().required("El Municipio es requerido"),
    neighborhood: Yup.string(),
  });
  const { data, callBack, firstLogin = true, cancel } = props;
  const address = getAgencyAddress(data?.address);
  const onSubmit = async (values, { setStatus }) => {
    setStatus(undefined);
    // email_contact is the agency's public email; "email" would change the owner's login.
    const newVal = {
      business_name: values.business_name,
      email_contact: values.email_contact,
      whatsapp: values.whatsapp,
      address: [
        {
          state: values.state,
          municipality: values.municipality,
          ...(values.neighborhood && { neighborhood: values.neighborhood }),
          street: values.street,
          num_exterior: values.num_exterior,
          num_interior: values.num_interior,
          type_address: AGENCY_ADDRESS_TYPE,
          ...(address?.id && { id: address?.id }),
        },
      ],
      type_address: AGENCY_ADDRESS_TYPE,
    };
    try {
      await updateBusiness(data?.id, newVal);
    } catch (err) {
      setStatus(`No se pudieron guardar los cambios. ${handleApiError(err).userMessage}`);
      return;
    }
    if (callBack) {
      callBack(newVal);
    }
  };

  const required = firstLogin && <span className="text-red-500">*</span>;

  return (
    <div>
      <Formik
        initialValues={{
          business_name: data?.business_name || "",
          email_contact: data?.email_contact || "",
          whatsapp: data?.whatsapp || "",
          street: address?.street || "",
          num_exterior: address?.num_exterior || "",
          num_interior: address?.num_interior || "",
          state: address?.state?.id || "",
          municipality: address?.municipality?.id || "",
          neighborhood: address?.neighborhood?.id || 0,
        }}
        validationSchema={realEstateSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, status, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
          <form onSubmit={handleSubmit} data-splitbee-event="account-edit-contact-save">
            <div className={fieldGridClassName}>
              <div className="md:col-span-2">
                <label htmlFor="business_name" className={labelClassName}>
                  Nombre
                  {required}
                </label>
                <input
                  id="business_name"
                  name="business_name"
                  type="text"
                  value={values.business_name}
                  onChange={handleChange}
                  className={`${inputClassName} font-bold`}
                />
                {errors.business_name && (
                  <div className={errorClassName}>{errors.business_name}</div>
                )}
              </div>

              <div>
                <label htmlFor="email_contact" className={labelClassName}>
                  Email
                </label>
                <input
                  id="email_contact"
                  name="email_contact"
                  type="email"
                  value={values.email_contact}
                  onChange={handleChange}
                  className={inputClassName}
                />
                {errors.email_contact && (
                  <div className={errorClassName}>{errors.email_contact}</div>
                )}
              </div>

              <div>
                <label htmlFor="whatsapp" className={labelClassName}>
                  Teléfono
                </label>
                <NumericFormat
                  id="whatsapp"
                  name="whatsapp"
                  value={values.whatsapp}
                  format="## #### ####"
                  onValueChange={({ value }) => {
                    setFieldValue("whatsapp", value);
                  }}
                  type="text"
                  className={inputClassName}
                />
                {errors.whatsapp && <div className={errorClassName}>{errors.whatsapp}</div>}
              </div>

              <div>
                <label htmlFor="street" className={labelClassName}>
                  Calle
                </label>
                <input
                  id="street"
                  name="street"
                  type="text"
                  value={values.street}
                  onChange={handleChange}
                  className={inputClassName}
                />
              </div>

              <div>
                <Select
                  id="neighborhood"
                  useOptions={() => useNeighborhoods({ municipality: values.municipality })}
                  callback={(e) => setFieldValue("neighborhood", e)}
                  useOptionsValue={values.municipality}
                  defaultValue={values.neighborhood}
                  name="neighborhood"
                  label="Colonia"
                  disabled={!values.municipality}
                />
              </div>

              <div>
                <label htmlFor="num_exterior" className={labelClassName}>
                  Número exterior
                </label>
                <NumericFormat
                  id="num_exterior"
                  name="num_exterior"
                  value={values.num_exterior}
                  onChange={handleChange}
                  placeholder="123"
                  type="text"
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="num_interior" className={labelClassName}>
                  Número interior
                </label>
                <NumericFormat
                  id="num_interior"
                  name="num_interior"
                  value={values.num_interior}
                  onChange={handleChange}
                  placeholder="123"
                  type="text"
                  className={inputClassName}
                />
              </div>

              <div>
                <Select
                  id="state"
                  useOptions={useStates}
                  callback={(e) => setFieldValue("state", e)}
                  defaultValue={values.state}
                  name="state"
                  label={<>Estado{required}</>}
                />
                {errors.state && <div className={errorClassName}>{errors.state}</div>}
              </div>

              <div>
                <Select
                  id="municipality"
                  useOptions={() => useMunicipalities({ state_id: values.state })}
                  callback={(e) => setFieldValue("municipality", e)}
                  useOptionsValue={values.state}
                  defaultValue={values.municipality}
                  name="municipality"
                  label={<>Municipio{required}</>}
                />
                {errors.municipality && <div className={errorClassName}>{errors.municipality}</div>}
              </div>
            </div>

            {status ? (
              <p role="alert" className="mt-4 text-sm text-red-600">
                {status}
              </p>
            ) : null}
            <div className="mt-6 flex flex-wrap gap-2">
              <Button type="submit" disabled={isSubmitting} className={primaryButtonClassName}>
                Guardar
              </Button>
              <Button
                type="button"
                variant="outline"
                className={outlineButtonClassName}
                onClick={cancel}
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

function Select(props) {
  const {
    useOptions,
    callback,
    label,
    name,
    defaultValue,
    useOptionsValue = undefined,
    ...rest
  } = props;

  const { data = [] } = useOptions(useOptionsValue);

  return (
    <>
      <label htmlFor={name} className={labelClassName}>
        {label}
      </label>
      {data.length ? (
        <select
          name={name}
          onChange={({ target }) => callback(target.value)}
          className={inputClassName}
          {...rest}
        >
          {data.map((x) => (
            <option key={x.id} value={x.id} selected={x.id === defaultValue}>
              {x.name}
            </option>
          ))}
        </select>
      ) : (
        <input
          name={name}
          type="text"
          onChange={({ target }) => callback(target.value)}
          className={inputClassName}
          {...rest}
        />
      )}
    </>
  );
}

BusinessInformation.defaultProps = {
  callBack: null,
};
